import { MapDataRoot } from '../../../app/maps/types'
import { ChartBoxoverEntry } from '../../../app/shared/components/ChartBoxover'
import { ColorVariant } from '../../components'
import type { ApiPaginatedResponse } from '../../types'

export const SEGMENT_COLORS = Object.keys(ColorVariant).filter(
  (c) => ![ColorVariant.gray, ColorVariant.default, ColorVariant.darkGray].includes(c as ColorVariant)
) as ColorVariant[]

export enum InvestorType {
  Manager = 'manager_13f',
  OtherManager = 'other_manager_13f',
  Fund = 'nport_fund',
}

export enum InsiderView {
  Index = '',
  Managers = 'managers',
  ManagerDetails = 'manager-details',
  ManagerHoldings = 'manager-holdings',
  Funds = 'funds',
  FundDetails = 'fund-details',
  FundHoldings = 'fund-holdings',
  NotFound = 'not-found',
}

export enum InsiderViewParam {
  View = 'view',
  Page = 'page',
  Search = 'search',
  InvestorId = 'investorId',
  ReportEndDate = 'reportEndDate',
  TransactionType = 'transactionType',
  OptionType = 'optionType',
  AssetCategory = 'assetCategory',
  Sort = 'sort',
  InvestorType = 'investorType',
}

export enum HoldingOptionType {
  Any = 'any',
  Share = 'share',
  Put = 'put',
  Call = 'call',
}

export const InsiderViewHasParams: Record<InsiderView, InsiderViewParam[]> = {
  [InsiderView.Index]: [InsiderViewParam.View],
  [InsiderView.Managers]: [
    InsiderViewParam.View,
    InsiderViewParam.Page,
    InsiderViewParam.Search,
    InsiderViewParam.Sort,
  ],
  [InsiderView.ManagerDetails]: [InsiderViewParam.View, InsiderViewParam.InvestorId, InsiderViewParam.ReportEndDate],
  [InsiderView.ManagerHoldings]: [
    InsiderViewParam.View,
    InsiderViewParam.InvestorId,
    InsiderViewParam.ReportEndDate,
    InsiderViewParam.TransactionType,
    InsiderViewParam.OptionType,
    InsiderViewParam.Page,
    InsiderViewParam.Sort,
  ],
  [InsiderView.Funds]: [InsiderViewParam.View, InsiderViewParam.Page, InsiderViewParam.Search, InsiderViewParam.Sort],
  [InsiderView.FundDetails]: [InsiderViewParam.View, InsiderViewParam.InvestorId, InsiderViewParam.ReportEndDate],
  [InsiderView.FundHoldings]: [
    InsiderViewParam.View,
    InsiderViewParam.InvestorId,
    InsiderViewParam.ReportEndDate,
    InsiderViewParam.TransactionType,
    InsiderViewParam.AssetCategory,
    InsiderViewParam.Page,
    InsiderViewParam.Sort,
  ],
  [InsiderView.NotFound]: [InsiderViewParam.View, InsiderViewParam.InvestorType],
}

export interface InsiderViewsParams {
  [InsiderView.Index]: Record<string, unknown>
  [InsiderView.Managers]: {
    [InsiderViewParam.View]: InsiderView
    [InsiderViewParam.Page]: number
    [InsiderViewParam.Search]?: string
    [InsiderViewParam.Sort]?: string
  }
  [InsiderView.ManagerDetails]: {
    [InsiderViewParam.View]: InsiderView
    [InsiderViewParam.InvestorId]: string
    [InsiderViewParam.ReportEndDate]?: string
  }
  [InsiderView.ManagerHoldings]: {
    [InsiderViewParam.View]: InsiderView
    [InsiderViewParam.InvestorId]: string
    [InsiderViewParam.ReportEndDate]?: string
    [InsiderViewParam.TransactionType]?: HoldingTransactionType
    [InsiderViewParam.OptionType]?: HoldingOptionType
    [InsiderViewParam.Page]: number
    [InsiderViewParam.Sort]?: string
  }
  [InsiderView.Funds]: {
    [InsiderViewParam.View]: InsiderView
    [InsiderViewParam.Page]: number
    [InsiderViewParam.Search]?: string
    [InsiderViewParam.Sort]?: string
  }
  [InsiderView.FundDetails]: {
    [InsiderViewParam.View]: InsiderView
    [InsiderViewParam.InvestorId]: string
    [InsiderViewParam.ReportEndDate]?: string
  }
  [InsiderView.FundHoldings]: {
    [InsiderViewParam.View]: InsiderView
    [InsiderViewParam.InvestorId]: string
    [InsiderViewParam.ReportEndDate]?: string
    [InsiderViewParam.TransactionType]?: HoldingTransactionType
    [InsiderViewParam.AssetCategory]?: string
    [InsiderViewParam.Page]: number
    [InsiderViewParam.Sort]?: string
  }
  [InsiderView.NotFound]: {
    [InsiderViewParam.View]: InsiderView
    [InsiderViewParam.InvestorType]: InvestorType
  }
}

export const INSIDER_VIEW_LABELS = {
  [InsiderView.Index]: 'Insiders',
  [InsiderView.Managers]: 'Managers',
  [InsiderView.Funds]: 'Funds',
}

export interface ManagerSearchItemData {
  investorId: string
  name: string
  seriesName?: string
}

export interface InstOwnFiler {
  investorId: string
  filerName: string
  filerCik?: number
  seriesName?: string | null
  investorType: InvestorType
  otherManagerName?: string
  hasOwnHoldings?: boolean
}

interface InstOwnLatestSummary {
  reportDate: string
  totalSumOfUsdValue: number
  turnoverPerc: number
}

export interface TopInvestmentType {
  ticker?: string
  name?: string
  value: number
  putCall: HoldingOptionType
  boxoverData: ChartBoxoverEntry | null
}

export interface ManagerFundItemData {
  filer: InstOwnFiler
  topInvestments: Array<TopInvestmentType & { percentageOfPortfolio: number }>
  latestSummaries: InstOwnLatestSummary[]
}

interface ManagerFundSummary {
  investorId: string
  reportDate: string
  totalCountOfInvestments: number
  totalSumOfUsdValue: number
  totalSumOfUsdValuePrevQ: number | null
  countOfNewPurchased: number
  countOfSoldOut: number
  countOfAddedTo: number
  countOfReduced: number
  percOfPortfolioOfTop10: number
  turnoverPerc: number
  timeHeldTop10: number
  timeHeldTop20: number
  timeHeldAll: number
  isConfidentialOmitted: boolean
}

export interface SectorAllocation {
  quarter: string
  sector: string
  pctAllocation: number
  usdAllocation: number
}

export type IndividualManagers = Array<{ name: string; investorId: string | null }>

export interface ManagerData {
  filer: InstOwnFiler
  latestSummary: ManagerFundSummary
  topBuysUsd: TopInvestmentType[]
  topSellsUsd: TopInvestmentType[]
  topBuysPct: TopInvestmentType[]
  topSellsPct: TopInvestmentType[]
  individualManagers: IndividualManagers
  mapData: MapDataRoot
  sectorAllocations: SectorAllocation[]
}

export interface InitialManagerDetailsData {
  initialManagerDetailsData: ManagerData
  initialManagerReportDates: string[]
}

export interface CountryAllocation {
  quarter: string
  country: string
  pctAllocation: number
  usdAllocation: number
}

export interface FundData {
  filer: InstOwnFiler
  latestSummary: ManagerFundSummary
  topBuysUsd: TopInvestmentType[]
  topSellsUsd: TopInvestmentType[]
  topBuysPct: TopInvestmentType[]
  topSellsPct: TopInvestmentType[]
  individualManagers: IndividualManagers
  mapData: MapDataRoot
  countryAllocations: CountryAllocation[]
}

export interface InitialFundDetailsData {
  initialFundDetailsData: FundData
  initialFundReportDates: string[]
}

export interface ManagerHolding {
  issuerTicker: string
  issuerName: string
  putCall: HoldingOptionType
  sector: string
  subAdvisorFlag: boolean
  shares: number
  valueUsd: number
  percentageOfPortfolio: number
  previousPercentageOfPortfolio: number
  changeInShares: number
  percentChangeFromPrevious: number
  lastClose: number
  firstQtrOwned: string
  percOwnership: number | null
  weightedAvgPrice: number | null
  boxoverData: ChartBoxoverEntry | null
}

export interface InitialManagerHoldingsData {
  initialManagerHoldingsFiler: InstOwnFiler | null
  initialManagerHoldingsData: ApiPaginatedResponse<ManagerHolding[]>
  initialManagerReportDates: string[]
  initialLatestSummary: {
    isConfidentialOmitted: boolean
  }
}

export interface FundHolding {
  boxoverData: ChartBoxoverEntry | null
  issuerName: string
  issuerTicker: string
  assetCategory: string
  investmentCountry: string
  payOffProfile: string
  percentChangeFromPrevious: number // % Chng. balance
  percentageOfPortfolio: number
  previousPercentageOfPortfolio: number
  shareType: string // Units
  shares: number // Balance
  valueUsd: number
}

export interface InitialFundHoldingsData {
  initialFundHoldingsFiler: InstOwnFiler | null
  initialFundHoldingsData: ApiPaginatedResponse<FundHolding[]>
  initialFundReportDates: string[]
  initialFundAssetCategories: Record<string, string>
}

export enum ManagerFundSortEnum {
  PortfolioValue = 'portfolio_value',
  NumInvestments = 'num_investments',
  NewPurchased = 'new_purchased',
  SoldOut = 'sold_out',
  Added = 'added',
  Reduced = 'reduced',
  TopTenConcentration = 'top_ten_concentration',
  Turnover = 'turnover',
  TimeHeldTop10 = 'time_held_top10',
  TimeHeldAll = 'time_held_all',
}

export const ManagerFundSortLabel = {
  [ManagerFundSortEnum.PortfolioValue]: 'Portfolio Value',
  [ManagerFundSortEnum.NumInvestments]: 'Number of Investments',
  [ManagerFundSortEnum.NewPurchased]: 'New Purchased',
  [ManagerFundSortEnum.SoldOut]: 'Sold Out',
  [ManagerFundSortEnum.Added]: 'Added',
  [ManagerFundSortEnum.Reduced]: 'Reduced',
  [ManagerFundSortEnum.TopTenConcentration]: 'Top 10 Concentration',
  [ManagerFundSortEnum.Turnover]: 'Turnover',
  [ManagerFundSortEnum.TimeHeldTop10]: 'Time Held (Top 10)',
  [ManagerFundSortEnum.TimeHeldAll]: 'Time Held (All)',
} as const

export const DEFAULT_QUERY_CACHE_TIME_IN_MS = 1000

export enum ChangeTypeEnum {
  Usd,
  Percentage,
}

export enum HoldingColumnEnum {
  ticker = 'issuerTicker',
  name = 'issuerName',
  type = 'putCall',
  sector = 'sector',
  sharesHeld = 'shares',
  marketValue = 'valueUsd',
  portfolioPercentage = 'percentageOfPortfolio',
  previousPortfolioPercentage = 'previousPercentageOfPortfolio',
  changeInShares = 'changeInShares',
  changeInSharesPercentage = 'percentChangeFromPrevious',
  ownershipPercentage = 'percOwnership',
  averagePrice = 'weightedAvgPrice',
  currentPrice = 'currentPrice',
  quarterFirstOwned = 'firstQtrOwned',
}

export enum FundHoldingColumnEnum {
  issuerTicker = 'issuerTicker',
  issuerName = 'issuerName',
  assetCategory = 'assetCategory',
  balance = 'shares',
  units = 'shareType',
  valueUsd = 'valueUsd',
  portfolioPercentage = 'percentageOfPortfolio',
  previousPortfolioPercentage = 'previousPercentageOfPortfolio',
  percentChangeFromPrevious = 'percentChangeFromPrevious',
  payOffProfile = 'payOffProfile',
  investmentCountry = 'investmentCountry',
}

export enum HoldingTransactionType {
  Any = 'any',
  New = 'new',
  ClosedOut = 'closed_out',
  Added = 'added',
  Reduced = 'reduced',
  NoChange = 'no_change',
}

export const HoldingTransactionTypeLabelMap = {
  [HoldingTransactionType.Any]: 'Any',
  [HoldingTransactionType.New]: 'New',
  [HoldingTransactionType.Added]: 'Added',
  [HoldingTransactionType.Reduced]: 'Reduced',
  [HoldingTransactionType.ClosedOut]: 'Closed Out',
  [HoldingTransactionType.NoChange]: 'No Change',
}

export const HoldingTransactionTypes = [
  HoldingTransactionType.Any,
  HoldingTransactionType.New,
  HoldingTransactionType.Added,
  HoldingTransactionType.Reduced,
  HoldingTransactionType.ClosedOut,
  HoldingTransactionType.NoChange,
]

export const HoldingOptionTypeLabelMap = {
  [HoldingOptionType.Any]: 'Any',
  [HoldingOptionType.Share]: 'Share',
  [HoldingOptionType.Put]: 'Put',
  [HoldingOptionType.Call]: 'Call',
}

export const HoldingOptionTypes = [
  HoldingOptionType.Any,
  HoldingOptionType.Share,
  HoldingOptionType.Put,
  HoldingOptionType.Call,
]
